import apiClient, { ApiClient } from './api-client';
import { config } from '../config/config';

export interface LoginCredentials {
    email: string;
    password: string;
}

export interface AuthResponse {
    token: string;
    user: {
        userId: number;
        email: string;
        username: string;
        clientId: number;
        subdomain: string;
        permissions: string[];
    };
}

export interface User {
    userId: number;
    email: string;
    username: string;
    clientId: number;
    subdomain: string;
    permissions: string[];
}

export type AuthProvider = 'GOOGLE' | 'MICROSOFT' | 'LOCAL';

export class AuthService {
    private currentUser: User | null = null;
    private apiClient: ApiClient;

    constructor() {
        this.apiClient = apiClient;
    }

    // Log in a user
    public async login(credentials: LoginCredentials): Promise<AuthResponse> {
        try {
            console.log('Login attempt with credentials:', { email: credentials.email });
            console.log('API base URL:', config.api.baseUrl);
            const response = await this.apiClient.post<AuthResponse>('/auth/login', credentials);
            if (response.token) {
                const user = { ...response.user, permissions: response.user.permissions ?? [] };
                localStorage.setItem('auth_token', response.token);
                localStorage.setItem('user', JSON.stringify(user));
                this.currentUser = user;
            }
            return { ...response, user: response.user ? { ...response.user, permissions: response.user.permissions ?? [] } : response.user };
        } catch (error) {
            console.error('Login error:', error);
            if (error instanceof Error) {
                throw new Error(`Login failed: ${error.message}`);
            }
            throw error;
        }
    }

    // Validate Google token
    public async loginWithGoogle(token: string): Promise<AuthResponse> {
        const response = await this.apiClient.post<AuthResponse>(config.api.endpoints.auth.validate, {
            provider: 'GOOGLE',
            token
        });
        
        if (response.token) {
            localStorage.setItem('auth_token', response.token);
            localStorage.setItem('authProvider', 'GOOGLE');
        }
        
        return response;
    }

    // Validate Microsoft token
    public async loginWithMicrosoft(token: string): Promise<AuthResponse> {
        const response = await this.apiClient.post<AuthResponse>(config.api.endpoints.auth.validate, {
            provider: 'MICROSOFT',
            token
        });
        
        if (response.token) {
            localStorage.setItem('auth_token', response.token);
            localStorage.setItem('authProvider', 'MICROSOFT');
        }
        
        return response;
    }

    // Check if user is logged in and token is valid
    public isAuthenticated(): boolean {
        return !!localStorage.getItem('auth_token');
    }

    // Get current user info
    public async getCurrentUser(): Promise<User | null> {
        try {
            if (this.currentUser) return this.currentUser;

            const storedUser = localStorage.getItem('user');
            if (storedUser) {
                const parsed = JSON.parse(storedUser);
                this.currentUser = { ...parsed, permissions: parsed.permissions ?? [] };
                return this.currentUser ?? null;
            }

            const response = await this.apiClient.get<User>(config.api.endpoints.auth.me);
            const user = { ...response, permissions: response.permissions ?? [] };
            this.currentUser = user;
            localStorage.setItem('user', JSON.stringify(user));

            return user;
        } catch (error) {
            if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
                this.logout();
                return null;
            }
            throw error;
        }
    }

    // Validate general token
    public async validateToken(): Promise<boolean> {
        try {
            const token = localStorage.getItem('auth_token');
            if (!token) return false;

            await this.apiClient.get(config.api.endpoints.auth.validate);
            return true;
        } catch (error) {
            if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
                this.logout();
                return false;
            }
            throw error;
        }
    }

    // Log out the user
    public logout(): void {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('authProvider');
        localStorage.removeItem('user');
        this.currentUser = null;
        this.apiClient.cancelAllRequests();
    }

    getStoredToken(): string | null {
        return localStorage.getItem('auth_token');
    }
}

export const authService = new AuthService();
export default authService; 